import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, type PillTab } from '@ah/design/mobile';
import { CalendarDays } from 'lucide-react';
import { GATHERINGS_QUERY, type Gathering } from '../shared/index.ts';
import { GatheringRow, GatheringsListSkeleton, ErrorRetry } from './_parts.tsx';

const TABS: PillTab[] = [
  { to: '/gatherings', label: 'Upcoming', end: true },
  { to: '/gatherings/attending', label: 'Attending' },
  { to: '/gatherings/past', label: 'Past' },
];

function isAttending(g: Gathering): boolean {
  return g.currentUserParticipant?.status === 'reserved';
}

function useGatherings() {
  const gql = useGql();
  return useQuery({
    queryKey: ['gatherings', 'list'],
    queryFn: async () => {
      const res = await gql.request<{ gatherings: Gathering[] }>(GATHERINGS_QUERY);
      return res.gatherings;
    },
  });
}

function GatheringsShell({
  children,
  onRefresh,
}: {
  children: React.ReactNode;
  onRefresh: () => Promise<unknown>;
}) {
  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Gatherings" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={onRefresh}>
        {children}
      </PullToRefresh>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <CalendarDays size={32} className="mx-auto mb-2 opacity-50" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function Gatherings() {
  const { data, isLoading, error, refetch } = useGatherings();
  const now = Date.now();
  const upcoming = data?.filter((g) => new Date(g.endsAt).getTime() >= now) ?? [];

  return (
    <GatheringsShell onRefresh={() => refetch()}>
      {isLoading ? (
        <GatheringsListSkeleton />
      ) : error ? (
        <ErrorRetry label="Could not load gatherings." onRetry={() => refetch()} />
      ) : upcoming.length === 0 ? (
        <EmptyState label="No upcoming gatherings." />
      ) : (
        <div className="space-y-3">
          {upcoming.map((g) => (
            <GatheringRow key={g.id} gathering={g} />
          ))}
        </div>
      )}
    </GatheringsShell>
  );
}

export function AttendingGatherings() {
  const { data, isLoading, error, refetch } = useGatherings();
  const now = Date.now();
  const attending = (data ?? []).filter(
    (g) => isAttending(g) && new Date(g.endsAt).getTime() >= now,
  );

  return (
    <GatheringsShell onRefresh={() => refetch()}>
      {isLoading ? (
        <GatheringsListSkeleton />
      ) : error ? (
        <ErrorRetry label="Could not load gatherings." onRetry={() => refetch()} />
      ) : attending.length === 0 ? (
        <EmptyState label="You're not signed up for any gatherings yet." />
      ) : (
        <div className="space-y-3">
          {attending.map((g) => (
            <GatheringRow key={g.id} gathering={g} />
          ))}
        </div>
      )}
    </GatheringsShell>
  );
}

export function PastGatherings() {
  const { data, isLoading, error, refetch } = useGatherings();
  const now = Date.now();
  const past = data?.filter((g) => new Date(g.endsAt).getTime() < now) ?? [];

  return (
    <GatheringsShell onRefresh={() => refetch()}>
      {isLoading ? (
        <GatheringsListSkeleton />
      ) : error ? (
        <ErrorRetry label="Could not load gatherings." onRetry={() => refetch()} />
      ) : past.length === 0 ? (
        <EmptyState label="No past gatherings." />
      ) : (
        <div className="space-y-3 opacity-80">
          {past.map((g) => (
            <GatheringRow key={g.id} gathering={g} />
          ))}
        </div>
      )}
    </GatheringsShell>
  );
}
